/**
 * 🔐 Helper central de autenticação (token + dados do usuário no localStorage)
 *
 * MOTIVAÇÃO:
 * Por mais de uma migração inacabada, conviviam DUAS chaves no localStorage:
 *   - 'auth_token' (canônica)
 *   - 'token'      (legado de uma migração antiga, ainda preenchida em alguns pontos)
 *
 * 97 lugares no código liam um ou outro, alguns liam um, outros liam ambos com
 * fallback. Sintoma: bug intermitente quando sessão era criada em um fluxo e
 * lida em outro.
 *
 * Esse helper centraliza. Regras:
 *   - `getToken()`: prefere 'auth_token'; cai em 'token' (sessões antigas);
 *     retorna null se nenhuma.
 *   - `setToken(t)`: grava em 'auth_token' E em 'token' (compat enquanto há
 *     consumidores legados que ainda leem 'token' direto).
 *   - `clearToken()`: limpa as DUAS chaves + 'user_data'.
 *
 * NOVO CÓDIGO deve usar SEMPRE getToken/setToken/clearToken — não acesse
 * localStorage direto pra token. ESLint pode bloquear no futuro.
 */

const PRIMARY_KEY = 'auth_token';
const LEGACY_KEY = 'token';
const USER_DATA_KEY = 'user_data';

export function getToken() {
  try {
    return localStorage.getItem(PRIMARY_KEY) || localStorage.getItem(LEGACY_KEY) || null;
  } catch {
    return null; // SSR ou storage indisponível
  }
}

export function setToken(token) {
  if (!token) return;
  try {
    localStorage.setItem(PRIMARY_KEY, token);
    localStorage.setItem(LEGACY_KEY, token); // compat com consumidores legados
  } catch {
    // ignora — não há fallback útil
  }
}

export function clearToken() {
  try {
    localStorage.removeItem(PRIMARY_KEY);
    localStorage.removeItem(LEGACY_KEY);
    localStorage.removeItem(USER_DATA_KEY);
  } catch {
    // ignora
  }
}

export function isAuthenticated() {
  return Boolean(getToken());
}

export function getUserData() {
  try {
    const raw = localStorage.getItem(USER_DATA_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setUserData(user) {
  try {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
  } catch {
    // ignora
  }
}

export default { getToken, setToken, clearToken, isAuthenticated, getUserData, setUserData };
